import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatAmountCommaSeperator,
	formatAmountToFloat,
	formatDate,
	useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BCEHTSettingService } from "../../services/BCEHTSettingService";
import { BcEhtDataGrid } from "./BcEhtDataGrid";
import { BcEhtHeaderColumns } from "./BcEhtDataGrid/BcEhtTableHeaderColumns";
import { BcEhtInfoSetupFieldsPanel } from "./Panels/BcEhtInfoSetupFieldsPanel";
import { BcEhtService } from "../../services/BcEhtService";
import { BcEhtSetupToolbar } from "./BcEhtSetupToolbar";
import { BcEhtSetupValidationSchema } from "./validator";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IBcEht } from "../../models/BcEht";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";

export interface IBcEhtSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "bcEht-single-data-record";
const BC_EHT_SETTING_BY_YEAR_LIST_DATA_QUERY_KEY =
	"bc-Eht-setting-by-year-single-data-record";
const BC_EHT_LIST_DATA_QUERY_KEY = "bcEht-list";

// const useGetData = (
// 	clientId: string,
// 	id: string | undefined,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
// 		queryFn: () => {
// 			return BcEhtService.getById(clientId, id, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const useBcEht = (
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
		queryFn: (data) => {
			return BcEhtService.getBcEht(clientId, taxYear, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetBcEhtSettingByYear = (
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [BC_EHT_SETTING_BY_YEAR_LIST_DATA_QUERY_KEY],
		queryFn: (data) => {
			return BCEHTSettingService.getById(taxYear, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

export const BcEhtSetup: React.FC<IBcEhtSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [bcEhtId, setBcEhtId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);
	const [bcEhtDataGridItems, setBcEhtDataGridItems] = useState([]);

	const [bcEhtSettingDataByYear, setBcEhtSettingDataByYear] =
		useState<any>(undefined);
	const { isPending: isPendingBcEhtSettingByYearg, data: bcEhtSettingByYear } =
		useGetBcEhtSettingByYear(taxYear, navigateToLogout);

	useEffect(() => {
		if (bcEhtSettingByYear) {
			if (bcEhtSettingByYear.message !== "Not Found" && bcEhtSettingByYear.id) {
				setBcEhtSettingDataByYear(bcEhtSettingByYear);
			} else {
				setBcEhtSettingDataByYear(undefined);
			}
		}
	}, [bcEhtSettingByYear]);

	const { isPending, data } = useBcEht(clientId, taxYear, navigateToLogout);

	const bcEhtList: any | null = data;

	const { isPending: isSubmitting, mutate: submitBcEhtFormFields } =
		useMutation({
			mutationFn: BcEhtService.create,
			onSuccess: (res: IBcEht | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIBcEhtResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/taxes/eht/bc_eht/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIBcEhtResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/taxes/eht/bc_eht/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	// const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
	// 	clientId,
	// 	editRecordId,
	// 	navigateToLogout
	// );

	const upadtedBcEhtListItems = useMemo(() => {
		if (
			bcEhtList &&
			bcEhtList.items &&
			bcEhtList.items.length > 0 &&
			bcEhtList.chooseAnOption
		) {
			const items = [...bcEhtList.items];

			// Find the last item with a non-null remitAmount using binary search
			let left = 0;
			let right = items.length - 1;
			let lastRemittedIndex = -1;

			while (left <= right) {
				let mid = Math.floor((left + right) / 2);
				if (items[mid].remitAmount != null) {
					lastRemittedIndex = mid;
					left = mid + 1; // look for later remitted value
				} else {
					right = mid - 1;
				}
			}

			// Find the first null remitAmount AFTER the last remitted one
			let nextNullIndex = -1;
			for (let i = lastRemittedIndex + 1; i < items.length; i++) {
				if (items[i].remitAmount == null) {
					nextNullIndex = i;
					break;
				}
			}
			// Map and add the two tags
			return items.map((item, index) => ({
				...item,
				// canDelete:
				// 	items[0].remitAmount === null ? false : index === lastRemittedIndex,
				// canEdit: items[0].remitAmount === null ? true : index === nextNullIndex,
				dueAmount: formatAmountCommaSeperator(item.dueAmount),
				canDelete: index === lastRemittedIndex,
				canEdit: index === nextNullIndex,
			}));
		} else if (
			bcEhtList &&
			bcEhtList.items &&
			bcEhtList.items.length > 0 &&
			!bcEhtList.chooseAnOption
		) {
			return null;
		} else if (bcEhtList && bcEhtList.ehtAmount === 0) {
			return null;
		} else if (
			bcEhtList &&
			bcEhtList.ehtAmount !== 0 &&
			!bcEhtList.installment
		) {
			return null;
			// let
		} else if (
			bcEhtList &&
			bcEhtList.ehtAmount !== 0 &&
			bcEhtList.installment
		) {
			let ehtAmountInstallment = bcEhtList.ehtAmount / 4;
			let rows = [
				{
					dueDate: "June 15",
					dueAmount: formatAmountCommaSeperator(ehtAmountInstallment),
					remitAmount: null,
					chequeNo: null,
					chequeDate: null,
					canEdit: true,
					canDelete: false,
				},
				{
					dueDate: "September 15",
					dueAmount: formatAmountCommaSeperator(ehtAmountInstallment),
					remitAmount: null,
					chequeNo: null,
					chequeDate: null,
					canEdit: false,
					canDelete: false,
				},
				{
					dueDate: "December 15",
					dueAmount: formatAmountCommaSeperator(ehtAmountInstallment),
					remitAmount: null,
					chequeNo: null,
					chequeDate: null,
					canEdit: false,
					canDelete: false,
				},
				{
					dueDate: "March 15",
					dueAmount: formatAmountCommaSeperator(ehtAmountInstallment),
					remitAmount: null,
					chequeNo: null,
					chequeDate: null,
					canEdit: false,
					canDelete: false,
				},
			];
			return rows;
		}
	}, [bcEhtList]);

	const formMethods = useForm<any>({
		resolver: yupResolver(BcEhtSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onSubmit = (values: any) => {
		let payload = {
			clientId: clientId,
			taxYear: taxYear,
			id: id && id,
			navigateToLogout: navigateToLogout,
			previousYearBc:
				values.previousYearBc && values.previousYearBc
					? values.previousYearBc
					: 0,
			currentYearExpectedBc:
				values.currentYearExpectedBc && values.currentYearExpectedBc
					? values.currentYearExpectedBc
					: 0,
			charitableOrg: values.charitableOrg,
			exemptionAmount: values.exemptionAmount,
			notchAmount: values.notchAmount,
			days: values.days ? values.days : 0,
			remitted: false,
			chooseAnOption: values.chooseAnOption ? values.chooseAnOption : false,
			installment:
				// values.chooseAnOption
				// 	? values.chooseAnOption
				// : 
				bcEhtList && bcEhtList.installment
					? bcEhtList.installment
					: false,
			notes: values.notes !== "" ? values.notes : undefined,
		};
		submitBcEhtFormFields(payload);
	};

	// useEffect(() => {
	// 	if (id && selectedDataRecord) {
	// 		Object.keys(selectedDataRecord).forEach((fieldName) => {
	// 			const fieldValue = selectedDataRecord[fieldName];
	// 			formMethods.setValue(fieldName, fieldValue ?? undefined);
	// 		});
	// 	}
	// }, [id, selectedDataRecord]);
	useEffect(() => {
		if (bcEhtList) {

			Object.keys(bcEhtList).forEach((fieldName) => {
				const fieldValue = bcEhtList[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "charitableOrg") {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
					if (fieldValue && bcEhtSettingDataByYear) {
						formMethods.setValue(
							"exemptionAmount",
							bcEhtSettingDataByYear.charitableExcemptionAmount
						);
						formMethods.setValue(
							"notchAmount",
							bcEhtSettingDataByYear.charitableNotchAmount
						);
					} else if (!fieldValue && bcEhtSettingDataByYear) {
						formMethods.setValue(
							"exemptionAmount",
							bcEhtSettingDataByYear.exemptionAmount
						);
						formMethods.setValue(
							"notchAmount",
							bcEhtSettingDataByYear.notchAmount
						);
					}
				}
				if (fieldName === "id") {
					let path = `/taxes/eht/bc_eht/${fieldValue}`;
					navigate(path);
					setEditRecordId(fieldValue);
				}
			});
		}
	}, [bcEhtList]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteBcEhtById } = useMutation({
		mutationFn: BcEhtService.delBcEht,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the BcEht fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/taxes/eht/bc_eht`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteBcEhtField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				bcEhtId: bcEhtId,
				navigateToLogout: navigateToLogout,
			};
			deleteBcEhtById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (bcEhtList) {
			setBcEhtId(bcEhtList.id);
		}
	};

	// const onRowDeleteClicked = useCallback((params: any) => {
	// 	setDialogOpen(true);
	// 	setSingleDelete(true);
	// 	setBcEhtId(params.id);
	// }, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	// const onResetClicked = () => {
	// 	setSnackbarData({
	// 		open: true,
	// 		message: "Success",
	// 		severity: "success",
	// 	});
	// 	formMethods.reset();
	// 	let path = `/taxes/eht/bc_eht`;
	// 	navigate(path);
	// };

	const onUpdateButtonClickedHandler = (
		exemptionAmount: string | number,
		notchAmount: string | number
	) => {

		formMethods.setValue("exemptionAmount", exemptionAmount);
		formMethods.setValue("notchAmount", notchAmount);
	};

	const expandedLabels = ["Taxes", "EHT"];
	const expandedBcEhtSetupOrSetupItem = "British Columbia"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Taxes" &&
				item.label === expandedBcEhtSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedBcEhtSetupOrSetupItem && "/taxes/eht/bc_eht",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const {
		isPending: isSubmittingBcEhtRemitData,
		mutate: submitBcEhtRemitData,
	} = useMutation({
		mutationFn: BcEhtService.remitBcEhtData,
		onSuccess: (res: IBcEht | IServerError | IResponse | any) => {

			if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				let path = `/taxes/eht/bc_eht/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else if (res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				let path = `/taxes/eht/bc_eht/${res.data.id}`;
				navigate(path);
				setEditRecordId(res.data.id);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [BC_EHT_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onRowRemitClicked = useCallback(
		(params: IBcEht | any) => {

			const updateBcEhtDataGridItems =
				params &&
				params.map((item: any) => ({
					...item,
					chequeDate: formatDate(item.chequeDate),
					canEdit: undefined,
					canDelete: undefined,
					remitted: item.canEdit
						? true
						: item.remitAmount !== null
							? true
							: false,
					remitAmount:
						item.canEdit && item.remitAmount === null ? 0 : item.remitAmount,
					dueAmount: formatAmountToFloat(item.dueAmount),
				}));

			let payload = {
				clientId: clientId,
				taxYear: taxYear,
				id: id ? id : bcEhtList && bcEhtList.id ? bcEhtList.id : undefined,
				navigateToLogout: navigateToLogout,
				items: updateBcEhtDataGridItems,
			};
			submitBcEhtRemitData(payload);
		},
		[bcEhtDataGridItems, editRecordId, bcEhtList]
	);

	const onRowDeleteClicked = useCallback((params: IBcEht | any) => {

		const updateBcEhtDataGridItems =
			params &&
			params.map((item: any) => ({
				...item,
				canDelete: undefined,
				canEdit: undefined,
				chequeDate: item.canDelete ? null : formatDate(item.chequeDate),
				chequeNo: item.canDelete ? null : item.chequeNo,
				dueAmount: formatAmountToFloat(item.dueAmount),
				dueDate: item.dueDate,
				remitAmount: item.canDelete ? null : item.remitAmount,
				remitted: item.canDelete
					? false
					: item.remitAmount !== null
						? true
						: false,
			}));

		let payload = {
			clientId: clientId,
			taxYear: taxYear,
			id: id ? id : bcEhtList && bcEhtList.id ? bcEhtList.id : undefined,
			navigateToLogout: navigateToLogout,
			items: updateBcEhtDataGridItems,
		};
		submitBcEhtRemitData(payload);
	}, [bcEhtList]);

	const onSubmitButtonClicked = useCallback(() => {
		// This triggers validation & calls onSubmit if valid
		formMethods.handleSubmit(onSubmit)();
	}, [formMethods, onSubmit]);

	return (
		<Grid>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					// isPendingById ||
					isDeleting ||
					isPendingBcEhtSettingByYearg ||
					isPending ||
					isSubmittingBcEhtRemitData
				}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteBcEhtField}
				cancelActionHandler={onCloseHandler}
				testId="bcEht-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="BcEht"
										testId={`bcEht`}
										// leftSpacing={1}
										// rightSpacing={1}
										topPadding={2}
										showTitle={false}
										variant="outlined"
									>
										<BcEhtInfoSetupFieldsPanel
											onUpdateClickedHandler={onUpdateButtonClickedHandler}
											bcEhtSettingDataByYear={bcEhtSettingDataByYear}
											formMethods={formMethods}
											bcEhtData={bcEhtList && !bcEhtList.message && bcEhtList}
										/>
									</StandardPanel>
								}
								toolbar={
									<BcEhtSetupToolbar
										// onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
										bcEhtData={bcEhtList && !bcEhtList.message && bcEhtList}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 2, lg: 2, xl: 2 }}></Grid>
						<Grid size={{ xs: 12, sm: 12, md: 6.5, lg: 6.5, xl: 6.5 }}>
							<BcEhtDataGrid
								columns={BcEhtHeaderColumns}
								rows={
									bcEhtList && upadtedBcEhtListItems
										? upadtedBcEhtListItems
										: []
								}
								onRowRemitClicked={onRowRemitClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onSubmitButtonClicked={onSubmitButtonClicked}
								ehtAmount={
									bcEhtList
										? formatAmountCommaSeperator(bcEhtList.ehtAmount)
										: "0.00"
								}
								installmentAmount={
									bcEhtList && bcEhtList.charitableOrg
										? formatAmountCommaSeperator(
											bcEhtSettingDataByYear &&
											bcEhtSettingDataByYear.installmentsFrom
										)
										: bcEhtList && !bcEhtList.charitableOrg
											? formatAmountCommaSeperator(
												bcEhtSettingDataByYear &&
												bcEhtSettingDataByYear.charitableInstallmentsFrom
											)
											: "0.00"
								}
								previousYearEmloyerEht={bcEhtList && bcEhtList.previousYearEmloyerEht}
								bcEhtDataGridItems={(data: any) => setBcEhtDataGridItems(data)}
								bcEhtData={bcEhtList && !bcEhtList.message && bcEhtList}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"bcEht-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
